// @ts-nocheck
import {useQuery} from '@tanstack/react-query';
import moment from 'moment';
import React, {useState} from 'react';
import {Flex, Text} from '@mantine/core';
import Fetch from '@/utils/Fetch';
import * as Enums from '@/utils/enums';
import SimpleTable from '@/PageComponents/SimpleTable/SimpleTable';
import EmployeeAvatar from '@/PageComponents/Table/EmployeeAvatar';
import ScStatusData from '@/PageComponents/Table/Table/ScStatusData';
import CellBool from '@/components/cells/bool';
import {previewFormDefinition} from '@/utils/form-definition-preview';
import {showNotification} from '@mantine/notifications';
import Helper from '@/utils/helper';
import ConfirmAction from '@/components/modals/confirm-action';
import {IconPrinter, IconArrowBackUp, IconEye} from '@tabler/icons-react';

interface FormDefinitionVersionHistoryProps {
    masterID: string;
    onPrintStateChange?: (printingID: string | null) => void;
    onReverted: (revertedFormDefinition: any) => void;
}

const FormDefinitionVersionHistory: React.FC<FormDefinitionVersionHistoryProps> = ({masterID, onPrintStateChange, onReverted}: any) => {

    const versionsQuery = useQuery(['formDefinitionVersions', masterID], () => Fetch.get({
        url: `/FormDefinition/${masterID}/Versions`
    }).then((response: any) => response?.Results ?? []), {
        enabled: !!masterID,
    });

    const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());
    const [reverting, setReverting] = useState(false);

    const printVersion = async (id: string) => {
        onPrintStateChange && onPrintStateChange(id);
        await previewFormDefinition(id, () => onPrintStateChange && onPrintStateChange(null));
    };

    const revertVersion = (draftID: string) => {
        setConfirmOptions({
            ...Helper.initialiseConfirmOptions(),
            display: true,
            heading: 'Revert to published version?',
            text: 'This will discard the unpublished changes in this draft and make the currently published version active again. This cannot be undone.',
            confirmButtonText: 'Revert to published version',
            onConfirm: async () => {
                setReverting(true);
                try {
                    const result = await Fetch.post({
                        url: '/FormDefinition/Revert',
                        params: {FormDefinitionID: draftID}
                    });
                    if (result?.ID) {
                        showNotification({
                            message: 'Reverted to the published version',
                            color: 'scBlue',
                            autoClose: 2000
                        });
                        versionsQuery.refetch();
                        onReverted(result);
                    } else {
                        showNotification({
                            message: result?.serverMessage || result?.message || 'Failed to revert',
                            color: 'yellow.7',
                        });
                        // The backend rejects a revert attempt whose premise is already stale (e.g. someone
                        // else discarded/reverted this same draft moments ago - see RevertToPublishedAsync's
                        // guard) - refetch so the list reflects whatever the real current state actually is,
                        // rather than leaving the user looking at the state that made them attempt this revert.
                        versionsQuery.refetch();
                    }
                } finally {
                    setReverting(false);
                }
            }
        });
    };

    return <>
        <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

        <SimpleTable
            data={versionsQuery.data || []}
            uniqueIdKey={'ID'}
            stylingProps={{compact: true, darkerText: true}}
            showControlsOnHover={false}
            onAction={(actionName: string, item: any) => {
                if (actionName === 'print') printVersion(item.ID);
                if (actionName === 'revert') revertVersion(item.ID);
            }}
            controls={[
                {name: 'print', icon: <IconEye size={16}/>, label: 'Preview this version'},
                {
                    name: 'revert',
                    icon: <IconArrowBackUp size={16}/>,
                    label: 'Revert to published version',
                    type: 'error',
                    // A Draft row is only "the current draft" - the one actually blocking Publish and open
                    // for editing - if it's also the newest version overall (a new draft is always created as
                    // maxVersion + 1, and further edits update that same row in place). Any other Draft-status
                    // row is a stale leftover from before this app enforced that invariant, and reverting it
                    // wouldn't do anything meaningful - it's not what's currently being edited.
                    showFunction: (item: any) => item.FormDefinitionStatus === Enums.FormDefinitionStatus.Draft
                        && item.Version === Math.max(...(versionsQuery.data ?? []).map((x: any) => x.Version)),
                    buttonProps: {loading: reverting},
                },
            ]}
            mapping={[
                {key: 'Version', label: 'Version', valueFunction: (x: any) => `Version ${x.Version}`},
                {key: 'ModifiedDate', label: 'Last edited', valueFunction: (x: any) => moment(x.ModifiedDate || x.CreatedDate).format('DD MMM, h:mmA')},
                {key: 'ModifiedBy', label: 'Updated by', valueFunction: (x: any) => <Flex align={'center'} gap={5}><EmployeeAvatar name={x.ModifiedBy || x.CreatedBy} /><Text size={'sm'}>{x.ModifiedBy || x.CreatedBy}</Text></Flex>},
                {
                    key: 'FormDefinitionStatus',
                    label: 'Status',
                    // Renders with the exact same ScStatusData pill ScTableCell's numeric 'status' case uses for
                    // this same column on the forms list (SettingsFormList), resolving the label from
                    // Enums.FormDefinitionStatus's key name (so the live status reads "Published") and
                    // the color from Enums.FormDefinitionStatusColor, whose values line up 1:1 with FormDefinitionStatus.
                    //
                    // Publishing promotes a row to Published in place rather than inserting a new one, so an
                    // older version can be left sitting at FormDefinitionStatus = Published after a newer draft
                    // has since become the actual published version. That's stale - only the row IsCurrentPublished
                    // flags is genuinely published - so any other Published row shows no status at all rather than
                    // a misleading "Published" pill.
                    valueFunction: (x: any) => {
                        if (x.FormDefinitionStatus === Enums.FormDefinitionStatus.Published && !x.IsCurrentPublished) {
                            return null;
                        }
                        return (
                            <ScStatusData
                                value={Object.entries(Enums.FormDefinitionStatus).find(([, val]: any) => val === x.FormDefinitionStatus)?.[0] || ''}
                                color={Object.entries(Enums.FormDefinitionStatusColor).find(([, val]: any) => val === x.FormDefinitionStatus)?.[0]}
                            />
                        );
                    },
                },
                {key: 'IsCurrentPublished', label: 'Current', alignCenter: true, valueFunction: (x: any) => x.IsCurrentPublished ? <CellBool value={true} /> : null},
            ]}
        />
    </>;
};

export default FormDefinitionVersionHistory;
